"use client";

import { useEffect, useRef, type Dispatch, type KeyboardEvent, type SetStateAction } from "react";
import { computeNextIndex, type PaletteItem } from "@/lib/commandPalette";

/** Arrow keys move the highlight, Enter performs it, Escape hands back to the caller. */
export function usePaletteKeyboard({
  items,
  selectedIndex,
  setSelectedIndex,
  onEscape,
}: {
  items: PaletteItem[];
  selectedIndex: number;
  setSelectedIndex: Dispatch<SetStateAction<number>>;
  onEscape: () => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);

  // Reconcile selection before rendering a shorter result list.
  const boundedIndex = Math.min(selectedIndex, Math.max(0, items.length - 1));
  if (selectedIndex !== boundedIndex) setSelectedIndex(boundedIndex);

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const selectedElement = listRef.current.querySelector(
      `[data-index="${selectedIndex}"]`
    );
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  // Keyboard navigation
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onEscape();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((idx) =>
        computeNextIndex(idx, items.length, "down")
      );
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((idx) =>
        computeNextIndex(idx, items.length, "up")
      );
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (items[selectedIndex]) {
        void items[selectedIndex].perform();
      }
    }
  };

  return { listRef, handleKeyDown };
}
